import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, CardMedia, CardActions, IconButton } from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ShareIcon from '@mui/icons-material/Share';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';

const ServiceCard = ({ service }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const { selectedTenantId } = useTenant();
    // Get event_id and tenant_id from URL params if needed
    const { event_id, tenant_id } = useParams(); // Uncomment if needed
    const [isParticipating, setIsParticipating] = useState(false);

    useEffect(() => {
        setIsParticipating(service.event_user_state?.is_participating || false);
    }, [service.event_user_state]);

    const handleParticipateClick = async () => {
        const answer = isParticipating ? 'not_available' : 'participate';
        setIsParticipating(!isParticipating);
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const payload = {
            answer: answer
        };
        await axios.post(
            `${BACKEND_URL}/app/t:${selectedTenantId}/api/v2/events/e:${event_id}/s:${service.service_id}/user-states/answers/`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch((error) => {
            console.error('Error updating participation status:', error);
            setIsParticipating(!isParticipating);
        });
    }

    const formatDate = (dateString) => {
        if (!dateString) return t("eventCard.eventDurationMessage", "Pendant toute la durée de l'evenement");
        try {
            const date = new Date(dateString);
            const day = date.toLocaleDateString(i18n.language, { weekday: 'long' });
            const dayNum = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleDateString(i18n.language, { month: 'long' });
            const year = date.getFullYear();
            const hour = date.getHours().toString().padStart(2, '0');
            const minute = date.getMinutes().toString().padStart(2, '0');
            return `${day.charAt(0).toUpperCase() + day.slice(1)} ${dayNum} ${month} ${year} à ${hour}:${minute}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Card style={{ maxWidth: 300, margin: '1rem' }}>
            {service.images && (
                <CardMedia
                    component="img"
                    height="140"
                    image={service.images}
                    alt={service.title}
                />
            )}
            <CardContent>
                <Typography gutterBottom variant="body2" sx={{ fontWeight: 'bold' }} component="div">
                    {service.title}
                </Typography>
                {isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarTodayOutlinedIcon sx={{ mr: 1, fontSize: '0.7rem' }} />
                        <Tooltip title={formatDate(service.start_time)} arrow>
                            <Typography 
                                variant="body2"
                                color="text.secondary"
                                sx={{ 
                                    fontSize: '0.7rem',
                                    textAlign: 'left', 
                                    fontWeight: 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    display: 'block'
                                }}
                            >
                            {formatDate(service.start_time)}
                            </Typography>
                        </Tooltip>
                    </Box>
                )}
                <Typography gutterBottom variant="body2" component="div">
                    {service.description}
                </Typography>
            </CardContent>
            <CardActions disableSpacing style={{ justifyContent: 'flex-end' }} >
                {service.is_need_booking && (
                    <IconButton 
                        aria-label={isParticipating ? "I no longer participate" : "I participate"} 
                        onClick={handleParticipateClick}
                    >
                        {isParticipating ? (
                            <ThumbUpAltIcon sx={{ fontSize: '1.2rem' }} />
                        ) : (
                            <ThumbUpOffAltIcon sx={{ fontSize: '1.2rem' }} />
                        )}
                    </IconButton>
                )}
                <IconButton aria-label="Share">
                    <ShareIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
                <IconButton aria-label="Zoom In">
                    <ZoomInIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
            </CardActions>
        </Card>
    );
};

ServiceCard.propTypes = {
  service: PropTypes.shape({
    title: PropTypes.string.isRequired,
    image: PropTypes.string,
  }).isRequired,
};

export default ServiceCard;
